import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, AlertCircle, Clock, Loader2, Download, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useNylasAuthStore } from '../store/nylasAuth';
import { getBackfillJobStatus, triggerBackfill, getCachedEmailCount, getLastWebhookTimestamp, BackfillJobStatus } from '../lib/nylasService';
import { isNylasDisabled } from '../lib/nylasCache';

export default function OutlookConnectionSettings() {
  const {
    isInitialized,
    isConnected,
    grant,
    connect,
    disconnect,
    initialize,
    isLoading,
  } = useNylasAuthStore();

  const [error, setError] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [backfillJobs, setBackfillJobs] = useState<BackfillJobStatus[]>([]);
  const [cachedCount, setCachedCount] = useState<number>(0);
  const [lastWebhook, setLastWebhook] = useState<string | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, []);

  useEffect(() => {
    if (isConnected) {
      loadBackfillStatus();
    }
  }, [isConnected]);

  const loadBackfillStatus = async () => {
    try {
      const [jobs, count, lastWh] = await Promise.all([
        getBackfillJobStatus(),
        getCachedEmailCount(),
        getLastWebhookTimestamp(),
      ]);
      setBackfillJobs(jobs);
      setCachedCount(count);
      setLastWebhook(lastWh);
    } catch {
      // Non-critical, silently fail
    }
  };

  const handleStartBackfill = async () => {
    setBackfillLoading(true);
    setBackfillError(null);
    try {
      await triggerBackfill('start');
      await loadBackfillStatus();
    } catch (err: any) {
      setBackfillError(err.message || 'Failed to start backfill');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleResumeBackfill = async (jobId: string) => {
    setBackfillLoading(true);
    setBackfillError(null);
    try {
      await triggerBackfill('resume', jobId);
      await loadBackfillStatus();
    } catch (err: any) {
      setBackfillError(err.message || 'Failed to resume backfill');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      await connect('/email');
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Outlook');
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      await disconnect();
      setShowDisconnectConfirm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect from Outlook');
    }
  };

  if (!isInitialized || isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mr-2" />
          <span className="text-gray-600">Loading connection settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Outlook Connection</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage your Outlook email and calendar integration
        </p>
      </div>

      <div className="px-6 py-4 space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                  isConnected() ? 'bg-green-100' : 'bg-gray-100'
                }`}
              >
                {isConnected() ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-gray-900">Connection Status</h4>
                <p className="text-sm text-gray-500">
                  {isConnected() ? 'Connected' : 'Not Connected'}
                </p>
              </div>
            </div>
            <div>
              {isConnected() ? (
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={handleConnect}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Connect to Outlook
                </button>
              )}
            </div>
          </div>
        </div>

        {isConnected() && grant && (
          <>
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Account Details</h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Email Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">{grant.email}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Provider</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{grant.provider}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Connected Since</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {format(new Date(grant.connected_at), 'MMM d, yyyy · h:mm a')}
                  </dd>
                </div>
                {grant.last_used_at && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">Last Used</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {format(new Date(grant.last_used_at), 'MMM d, yyyy · h:mm a')}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="rounded-md bg-blue-50 p-4">
                <div className="flex">
                  <Clock className="h-5 w-5 text-blue-400 mr-2" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Persistent Connection</p>
                    <p className="mt-1">
                      Your Outlook account stays connected via Nylas. You won't need
                      to reconnect unless you manually disconnect.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Sync / Backfill Section */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Email Cache Status</h4>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500 uppercase">Cached Emails</p>
                  <p className="text-lg font-semibold text-gray-900">{cachedCount.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500 uppercase">Last Webhook</p>
                  <p className="text-sm font-medium text-gray-900">
                    {lastWebhook ? format(new Date(lastWebhook), 'MMM d, h:mm a') : 'None'}
                  </p>
                </div>
              </div>

              {backfillError && (
                <div className="mb-3 rounded-md bg-red-50 p-3">
                  <p className="text-sm text-red-700">{backfillError}</p>
                </div>
              )}

              {/* Active/Recent backfill jobs */}
              {backfillJobs.length > 0 && (
                <div className="mb-4 space-y-2">
                  {backfillJobs.slice(0, 3).map((job) => (
                    <div key={job.id} className="flex items-center justify-between bg-gray-50 rounded-md p-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {job.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {job.status === 'in_progress' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                          {job.status === 'paused' && <Clock className="h-4 w-4 text-amber-500" />}
                          {job.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                          <span className="text-sm font-medium capitalize">{job.status.replace('_', ' ')}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {job.messages_fetched} messages · {job.pages_processed} pages
                          {job.retry_after_seconds && job.status === 'paused' && (
                            <span className="text-amber-600 ml-1">
                              · Rate limited, retry in {job.retry_after_seconds}s
                            </span>
                          )}
                        </p>
                      </div>
                      {job.status === 'paused' && (
                        <button
                          onClick={() => handleResumeBackfill(job.id)}
                          disabled={backfillLoading || isNylasDisabled()}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Resume
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Backfill trigger button */}
              <button
                onClick={handleStartBackfill}
                disabled={backfillLoading || isNylasDisabled() || backfillJobs.some(j => j.status === 'in_progress' || j.status === 'paused')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full justify-center"
                title={isNylasDisabled() ? 'Available after maintenance window completes' : undefined}
              >
                {backfillLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isNylasDisabled()
                  ? 'Sync Unavailable (Maintenance)'
                  : backfillJobs.some(j => j.status === 'in_progress' || j.status === 'paused')
                    ? 'Backfill In Progress...'
                    : 'Sync Last 30 Days'}
              </button>

              {isNylasDisabled() && (
                <p className="mt-2 text-xs text-amber-600">
                  Email sync is temporarily paused during a provider maintenance window. 
                  This will automatically become available once maintenance completes.
                </p>
              )}

              <button
                onClick={loadBackfillStatus}
                className="mt-2 inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh status
              </button>
            </div>
          </>
        )}
      </div>

      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Disconnect Outlook?</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to disconnect your Outlook account? You will need to
              reconnect to access your emails and calendar events.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
